import {
  Badge,
  Button,
  Flex,
  Table,
  Text,
  Section,
  TextField,
} from "@radix-ui/themes";
import { useCallback, useMemo, useState } from "react";
import { useGetSimulators } from "@/api/useRelease";
import { toast } from "sonner";
import { DropdownMenu } from "@/components/shared/dropdown-menu";
import {
  DotsVerticalIcon,
  Pencil1Icon,
  TrashIcon,
  PlayIcon,
  PlusIcon,
  MagnifyingGlassIcon,
} from "@radix-ui/react-icons";
import { useNavigate } from "react-router-dom";
import {
  Release,
  Simulator,
  simulatorChannelToLabel,
} from "@/api/simulator/simulator.model";
import Select from "@/components/shared/select";
import { Dialog } from "@/components/shared/dialog";
import { XIcon } from "lucide-react";
import { useRegressionApi } from "@/v2/entities/regression/Regression.api";
import { useMutation, useQuery } from "react-query";
import {
  RegressionSet,
  UpdateRegressionSetPayload,
} from "@/v2/entities/regression/Regression.model";
import { StructureCheck } from "@/v2/assets/icons/StructureCheck";
import { PageLayoutV2 } from "@/v2/components/layout/page-layout/PageLayout";
import { PageHeader } from "@/v2/components/layout";
import { PageColors, ENavigate } from "@/components/sidebar/sidebar.types";
import { useFlag } from "@/v2/components/feature-flag/FeatureFlagContext";

export function RegressionSets() {
  const {
    getRegressionSets,
    createRegressionSet,
    runRegressionTestBySet,
    runRegressionTestByTargetRelease,
  } = useRegressionApi();

  const { data, refetch } = useQuery({
    queryKey: ["regression-sets"],
    queryFn: getRegressionSets,
  });

  const {
    mutateAsync: createRegressionSetMutation,
    isLoading: createRegressionSetLoading,
  } = useMutation({
    mutationKey: ["create-regression-set"],
    mutationFn: createRegressionSet,
  });

  const {
    mutateAsync: runRegressionTestBySetMutation,
    isLoading: runRegressionTestBySetLoading,
  } = useMutation({
    mutationKey: ["run-regression-test-by-set"],
    mutationFn: runRegressionTestBySet,
  });

  const {
    mutateAsync: runRegressionTestByTargetReleaseMutation,
    isLoading: runRegressionTestByTargetReleaseLoading,
  } = useMutation({
    mutationKey: ["run-regression-test-by-target-release"],
    mutationFn: runRegressionTestByTargetRelease,
  });

  const { data: simulators } = useGetSimulators(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [runTestDialogOpen, setRunTestDialogOpen] = useState(false);
  const [runAutoTestDialogOpen, setRunAutoTestDialogOpen] = useState(false);
  const [selectedSetId, setSelectedSetId] = useState<string>("");
  const [selectedReleaseId, setSelectedReleaseId] = useState<number>(0);
  const [selectedSimulatorId, setSelectedSimulatorId] = useState<string>("0");
  const [targetReleaseId, setTargetReleaseId] = useState<number>(0);
  const [baselineReleaseId, setBaselineReleaseId] = useState<number>(0);
  const [numberOfConversations, setNumberOfConversations] =
    useState<number>(100);
  const [runTestAuiParameter, setRunTestAuiParameter] = useState(false);
  const [setName, setSetName] = useState("");
  const [description, setDescription] = useState("");
  const [searchPhrase, setSearchPhrase] = useState("");
  const navigate = useNavigate();
  const auiRegressionTestsEnabled = useFlag("aui-regression-tests");
  const handleRunTestDialogOpenChange = useCallback((open: boolean) => {
    setRunTestDialogOpen(open);
    if (!open) {
      setRunTestAuiParameter(false);
    }
  }, []);

  const handleCreateRegressionSet = async () => {
    try {
      await createRegressionSetMutation({
        name: setName,
        description,
      });

      toast("Regression set created successfully");
      setDialogOpen(false);
      setSetName("");
      setDescription("");
      refetch();
    } catch (error) {
      toast.error("Failed to create regression set");
    }
  };

  const handleRunTest = async () => {
    if (!selectedSetId || !selectedReleaseId) return;

    try {
      const result = await runRegressionTestBySetMutation({
        regressionSetId: selectedSetId,
        releaseId: selectedReleaseId,
        ...(runTestAuiParameter ? { auiParameter: true } : {}),
      });

      toast.success(`Test ${result?.regressionTestId} completed`);
      setRunTestDialogOpen(false);
      setSelectedSetId("");
      setSelectedReleaseId(0);
      setRunTestAuiParameter(false);
    } catch (error) {
      toast.error("Failed to run regression test");
    }
  };

  const handleRunAutoTest = async () => {
    if (!targetReleaseId) return;

    try {
      await runRegressionTestByTargetReleaseMutation({
        releaseToTestId: targetReleaseId,
        releaseToTestAgainstId: baselineReleaseId || undefined,
        numberOfConversations: numberOfConversations,
      });

      toast(
        `Auto-generated test completed with ${numberOfConversations} conversations`
      );
      setRunAutoTestDialogOpen(false);
      setTargetReleaseId(0);
      setBaselineReleaseId(0);
      setNumberOfConversations(100);
      navigate("/automation-center/regression-tests");
    } catch (error) {
      toast.error("Failed to run auto-generated regression test");
    }
  };

  return (
    <PageLayoutV2>
      <Flex direction="column">
        <PageHeader
          title="Regression Sets"
          description="Organize and test your AI agent responses with regression sets"
          icon={
            <StructureCheck
              size={24}
              fill={PageColors[ENavigate.REGRESSION_SETS] || "gray"}
            />
          }
          actions={
            <Flex gap="2" align="center">
              <TextField.Root
                placeholder="Search regression sets"
                size="2"
                value={searchPhrase}
                onChange={(e) => setSearchPhrase(e.target.value)}
              >
                <TextField.Slot>
                  <MagnifyingGlassIcon height="16" width="16" />
                </TextField.Slot>
              </TextField.Root>
              <Button
                variant="soft"
                color="gray"
                onClick={() => navigate("/automation-center/regression-tests")}
              >
                View Test Results
              </Button>
              <Button
                variant="soft"
                color="blue"
                onClick={() => setRunAutoTestDialogOpen(true)}
              >
                Run Auto-Generated Test
              </Button>
              <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
                <Dialog.Trigger>
                  <Button highContrast color="gray">
                    <PlusIcon />
                    Create Regression Set
                  </Button>
                </Dialog.Trigger>
                <Dialog.Content className="w-[450px] p-4">
                  <Dialog.Title>Create New Regression Set</Dialog.Title>
                  <Dialog.Close className=" absolute right-4 top-4">
                    <Button variant="ghost" color="gray" highContrast>
                      <XIcon />
                    </Button>
                  </Dialog.Close>
                  <Flex direction="column" gap="3" mt="4">
                    <Text size="2" mb="4">
                      Add a new regression set to organize your test questions
                      and expected answers.
                    </Text>
                    <Flex direction="column" gap="3">
                      <label>
                        <Text as="div" size="2" mb="1" weight="bold">
                          Name
                        </Text>
                        <input
                          className="w-full rounded-md border border-gray-300 px-3 py-2 focus:outline-none focus:ring-2 focus:ring-gray-200"
                          placeholder="Enter regression set name"
                          value={setName}
                          onChange={(e) => setSetName(e.target.value)}
                        />
                      </label>

                      <label>
                        <Text as="div" size="2" mb="1" weight="bold">
                          Description
                        </Text>
                        <textarea
                          className="w-full rounded-md border border-gray-300 px-3 py-2 focus:outline-none focus:ring-2 focus:ring-gray-200"
                          placeholder="Enter regression set description"
                          value={description}
                          onChange={(e) => setDescription(e.target.value)}
                        />
                      </label>
                    </Flex>

                    <Flex gap="3" mt="4" justify="end">
                      <Button
                        variant="soft"
                        color="gray"
                        onClick={() => setDialogOpen(false)}
                      >
                        Cancel
                      </Button>
                      <Button
                        color="gray"
                        highContrast
                        onClick={handleCreateRegressionSet}
                        disabled={createRegressionSetLoading}
                      >
                        {createRegressionSetLoading
                          ? "Creating..."
                          : "Create Regression Set"}
                      </Button>
                    </Flex>
                  </Flex>
                </Dialog.Content>
              </Dialog>

              {/* Run Auto-Generated Test Dialog */}
              <Dialog
                open={runAutoTestDialogOpen}
                onOpenChange={setRunAutoTestDialogOpen}
              >
                <Dialog.Content className="w-[450px] p-4">
                  <Dialog.Title>
                    Run Auto-Generated Regression Test
                  </Dialog.Title>
                  <Dialog.Close className="absolute right-4 top-4">
                    <Button variant="ghost" color="gray" highContrast>
                      <XIcon />
                    </Button>
                  </Dialog.Close>
                  <Flex direction="column" gap="3" mt="4">
                    <Text size="2" mb="4">
                      Compare responses from a target release against a baseline
                      release using historical conversation data.
                    </Text>
                    <Flex direction="column" gap="3">
                      <label>
                        <Text as="div" size="2" mb="1" weight="bold">
                          AI Agent
                        </Text>
                        <Select
                          value={String(selectedSimulatorId)}
                          onChange={(value) => setSelectedSimulatorId(value)}
                          options={simulators?.map((simulator: Simulator) => ({
                            label: `${simulator.name} (${
                              simulatorChannelToLabel[simulator.channel]
                            })`,
                            value: String(simulator.id),
                          }))}
                        />
                      </label>

                      <label>
                        <Text as="div" size="2" mb="1" weight="bold">
                          Target Release (to test)
                        </Text>
                        <Select
                          value={String(targetReleaseId)}
                          onChange={(value) =>
                            setTargetReleaseId(Number(value))
                          }
                          options={simulators
                            ?.find(
                              (simulator: Simulator) =>
                                simulator.id === Number(selectedSimulatorId)
                            )
                            ?.releases.sort(
                              (a: Release, b: Release) => a.id - b.id
                            )
                            ?.map((release: Release, index: number) => ({
                              label: `v${index + 1}`,
                              value: String(release.id),
                            }))}
                        />
                      </label>

                      <label>
                        <Text as="div" size="2" mb="1" weight="bold">
                          Baseline Release (optional - leave empty for
                          comparison with historical data)
                        </Text>
                        <Select
                          value={String(baselineReleaseId)}
                          onChange={(value) =>
                            setBaselineReleaseId(Number(value))
                          }
                          options={[
                            { label: "Use historical data", value: "0" },
                            ...(simulators
                              ?.find(
                                (simulator: Simulator) =>
                                  simulator.id === Number(selectedSimulatorId)
                              )
                              ?.releases.sort(
                                (a: Release, b: Release) => a.id - b.id
                              )
                              ?.map((release: Release, index: number) => ({
                                label: `v${index + 1}`,
                                value: String(release.id),
                              })) || []),
                          ]}
                        />
                      </label>

                      <label>
                        <Text as="div" size="2" mb="1" weight="bold">
                          Number of Conversations
                        </Text>
                        <input
                          type="number"
                          className="w-full rounded-md border border-gray-300 px-3 py-2 focus:outline-none focus:ring-2 focus:ring-gray-200"
                          placeholder="100"
                          value={numberOfConversations}
                          onChange={(e) =>
                            setNumberOfConversations(Number(e.target.value))
                          }
                          min="1"
                          max="1000"
                        />
                      </label>
                    </Flex>

                    <Flex gap="3" mt="4" justify="end">
                      <Button
                        variant="soft"
                        color="gray"
                        onClick={() => setRunAutoTestDialogOpen(false)}
                      >
                        Cancel
                      </Button>
                      <Button
                        color="blue"
                        highContrast
                        onClick={handleRunAutoTest}
                        disabled={
                          !targetReleaseId ||
                          numberOfConversations < 1 ||
                          runRegressionTestByTargetReleaseLoading
                        }
                      >
                        {runRegressionTestByTargetReleaseLoading
                          ? "Running..."
                          : "Run Auto-Generated Test"}
                      </Button>
                    </Flex>
                  </Flex>
                </Dialog.Content>
              </Dialog>
            </Flex>
          }
        />

        <Section size="4" pt="0">
          <Flex p="6" direction="column" gap="3" width="100%">
            <Flex direction={"row"} gap={"2"} align="center">
              <Flex gap={"2"} align={"center"}>
                <Text size={"3"} weight={"medium"}>
                  Regression Sets
                </Text>
                <Badge color="gray">{data?.length}</Badge>
              </Flex>
            </Flex>

            <Table.Root>
              <Table.Header>
                <Table.Row className=" text-gray-500 font-normal">
                  <Table.ColumnHeaderCell className=" font-normal w-1/4">
                    Name
                  </Table.ColumnHeaderCell>
                  <Table.ColumnHeaderCell className=" font-normal w-1/3">
                    Description
                  </Table.ColumnHeaderCell>
                  <Table.ColumnHeaderCell className=" font-normal w-1/6">
                    Items
                  </Table.ColumnHeaderCell>
                  <Table.ColumnHeaderCell className=" font-normal w-1/6">
                    Created
                  </Table.ColumnHeaderCell>
                  <Table.ColumnHeaderCell className=" font-normal w-1/12"></Table.ColumnHeaderCell>
                </Table.Row>
              </Table.Header>
              <Table.Body>
                {data?.length === 0 && (
                  <Table.Row>
                    <Table.Cell colSpan={5}>
                      <Flex
                        align="center"
                        justify="center"
                        py="6"
                        direction="column"
                        gap="2"
                      >
                        <Text size="2" color="gray">
                          No regression sets found
                        </Text>
                        <Text size="2" color="gray">
                          Create a new regression set to get started
                        </Text>
                        <Button
                          color="gray"
                          highContrast
                          onClick={() => setDialogOpen(true)}
                          className="mt-2"
                        >
                          Create Regression Set
                        </Button>
                      </Flex>
                    </Table.Cell>
                  </Table.Row>
                )}
                {data
                  ?.filter(
                    (set) =>
                      set.name
                        .toLowerCase()
                        .includes(searchPhrase.toLowerCase()) ||
                      (set.description &&
                        set.description
                          .toLowerCase()
                          .includes(searchPhrase.toLowerCase()))
                  )
                  .map((set) => (
                    <RegressionSetItem
                      key={set.id}
                      regressionSet={set}
                      refetch={refetch}
                      simulators={simulators || []}
                      auiRegressionTestsEnabled={auiRegressionTestsEnabled}
                      onRunTest={(setId, releaseId, auiParameter = false) => {
                        setSelectedSetId(setId);
                        setSelectedReleaseId(releaseId);
                        setRunTestAuiParameter(auiParameter);
                        setRunTestDialogOpen(true);
                      }}
                    />
                  ))}
              </Table.Body>
            </Table.Root>
          </Flex>
        </Section>

        <Dialog
          open={runTestDialogOpen}
          onOpenChange={handleRunTestDialogOpenChange}
        >
          <Dialog.Content size="small" isPortal={false} className=" p-4">
            <Dialog.Title>Run Regression Test</Dialog.Title>
            <Flex direction="column" gap="3" mt="4">
              <Text size="2" mb="4">
                Select a release to test against this regression set
              </Text>
              <label>
                <Text as="div" size="2" mb="1" weight="bold">
                  AI Agent
                </Text>
                <Select
                  // className="w-full rounded-md border border-gray-300 px-3 py-2 focus:outline-none focus:ring-2 focus:ring-gray-200"
                  value={String(selectedSimulatorId)}
                  onChange={(value) => setSelectedSimulatorId(value)}
                  options={simulators?.map((simulator: Simulator) => ({
                    label: `${simulator.name} (${
                      simulatorChannelToLabel[simulator.channel]
                    })`,
                    value: String(simulator.id),
                  }))}
                />
                <Text as="div" size="2" mb="1" weight="bold">
                  Release
                </Text>
                <Select
                  value={String(selectedReleaseId)}
                  onChange={(value) => setSelectedReleaseId(Number(value))}
                  options={simulators
                    ?.find(
                      (simulator: Simulator) =>
                        simulator.id === Number(selectedSimulatorId)
                    )
                    ?.releases.sort((a: Release, b: Release) => a.id - b.id)
                    ?.map((release: Release, index: number) => ({
                      label: `v${index + 1}`,
                      value: String(release.id),
                    }))}
                />
              </label>
            </Flex>

            <Flex gap="3" mt="4" justify="end">
              <Button
                color="gray"
                highContrast
                onClick={handleRunTest}
                disabled={!selectedReleaseId || runRegressionTestBySetLoading}
              >
                {runRegressionTestBySetLoading ? "Running..." : "Run Test"}
              </Button>
            </Flex>
          </Dialog.Content>
        </Dialog>
      </Flex>
    </PageLayoutV2>
  );
}

interface RegressionSetItemProps {
  regressionSet: RegressionSet;
  refetch: () => void;
  simulators: Simulator[];
  auiRegressionTestsEnabled: boolean;
  onRunTest: (
    setId: string,
    releaseId: number,
    auiParameter?: boolean
  ) => void;
}

function RegressionSetItem({
  regressionSet,
  refetch,
  simulators,
  auiRegressionTestsEnabled,
  onRunTest,
}: RegressionSetItemProps) {
  const { deleteRegressionSet, updateRegressionSet } = useRegressionApi();
  const {
    mutateAsync: deleteRegressionSetMutation,
    isLoading: deleteRegressionSetLoading,
  } = useMutation({
    mutationKey: ["delete-regression-set"],
    mutationFn: deleteRegressionSet,
  });
  const {
    mutateAsync: updateRegressionSetMutation,
    isLoading: updateRegressionSetLoading,
  } = useMutation({
    mutationKey: ["update-regression-set"],
    mutationFn: ({
      regressionSetId,
      input,
    }: {
      regressionSetId: string;
      input: UpdateRegressionSetPayload;
    }) => updateRegressionSet(regressionSetId, input),
  });

  const [dialogOpen, setDialogOpen] = useState(false);
  const [setName, setSetName] = useState(regressionSet.name);
  const [description, setDescription] = useState(
    regressionSet.description || ""
  );
  const navigate = useNavigate();

  const handleUpdateRegressionSet = async () => {
    try {
      await updateRegressionSetMutation({
        regressionSetId: regressionSet.id,
        input: {
          name: setName,
          description,
        },
      });

      toast("Regression set updated successfully");
      setDialogOpen(false);
      refetch();
    } catch (error) {
      toast.error("Failed to update regression set");
    }
  };

  const handleOpenEditDialog = useCallback(() => {
    setDialogOpen(true);
  }, []);

  const handleDropdownRunTest = useCallback(() => {
    if (simulators?.length > 0) {
      onRunTest(regressionSet.id, simulators[0].id);
    }
  }, [simulators, onRunTest, regressionSet.id]);

  const handleDropdownRunAuiTest = useCallback(() => {
    if (simulators?.length > 0) {
      onRunTest(regressionSet.id, simulators[0].id, true);
    }
  }, [simulators, onRunTest, regressionSet.id]);

  const handleDropdownDelete = useCallback(async () => {
    if (deleteRegressionSetLoading) return;
    try {
      await deleteRegressionSetMutation(regressionSet.id);
      toast("Regression set deleted successfully");
      refetch();
    } catch (error) {
      toast.error("Failed to delete regression set");
    }
  }, [
    deleteRegressionSetLoading,
    deleteRegressionSetMutation,
    regressionSet.id,
    refetch,
  ]);

  const rowDropdownItems = useMemo(() => {
    const items = [
      {
        label: "Edit",
        icon: <Pencil1Icon />,
        onClick: handleOpenEditDialog,
      },
      {
        label: "Run Test",
        icon: <PlayIcon />,
        onClick: handleDropdownRunTest,
      },
    ];
    if (auiRegressionTestsEnabled) {
      items.push({
        label: "Run AUI test",
        icon: <PlayIcon />,
        onClick: handleDropdownRunAuiTest,
      });
    }
    items.push({
      label: deleteRegressionSetLoading ? "Deleting..." : "Delete",
      icon: <TrashIcon />,
      onClick: handleDropdownDelete,
    });
    return items;
  }, [
    auiRegressionTestsEnabled,
    deleteRegressionSetLoading,
    handleOpenEditDialog,
    handleDropdownRunTest,
    handleDropdownRunAuiTest,
    handleDropdownDelete,
  ]);

  return (
    <Table.Row
      className="cursor-pointer hover:bg-gray-100"
      onClick={() =>
        navigate(`/automation-center/regression-sets/${regressionSet.id}`)
      }
    >
      <Table.Cell>
        <Flex align={"center"} gap={"2"}>
          <Flex
            align="center"
            justify="center"
            style={{
              width: "20px",
              height: "20px",
              backgroundColor: "var(--sky-3)",
              borderRadius: "4px",
            }}
          >
            <StructureCheck size={16} fill="sky" />
          </Flex>
          <Text size={"2"} color="gray" weight="medium" highContrast>
            {regressionSet.name}
          </Text>
        </Flex>
      </Table.Cell>
      <Table.Cell>{regressionSet.description || "-"}</Table.Cell>
      <Table.Cell>
        <Badge color="gray" variant="soft">
          {regressionSet.items?.length || 0} items
        </Badge>
      </Table.Cell>
      <Table.Cell>
        {new Date(regressionSet.createdAt).toLocaleDateString()}
      </Table.Cell>
      <Table.Cell onClick={(e) => e.stopPropagation()}>
        <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
          <DropdownMenu items={rowDropdownItems}>
            <DropdownMenu.Trigger>
              <Button variant="ghost" size="1" color="gray" highContrast>
                <DotsVerticalIcon />
              </Button>
            </DropdownMenu.Trigger>
          </DropdownMenu>

          <Dialog.Content>
            <Dialog.Title>Edit Regression Set</Dialog.Title>
            <Text>Update the details of your regression set.</Text>

            <Flex direction="column" gap="3">
              <label>
                <Text as="div" size="2" mb="1" weight="bold">
                  Name
                </Text>
                <input
                  value={setName}
                  onChange={(e) => setSetName(e.target.value)}
                  className="w-full rounded-md border border-gray-300 px-3 py-2"
                />
              </label>

              <label>
                <Text as="div" size="2" mb="1" weight="bold">
                  Description
                </Text>
                <textarea
                  value={description}
                  onChange={(e) => setDescription(e.target.value)}
                  className="w-full rounded-md border border-gray-300 px-3 py-2"
                />
              </label>
            </Flex>

            <Flex gap="3" mt="4" justify="end">
              <Dialog.Close>
                <Button variant="soft" color="gray">
                  Cancel
                </Button>
              </Dialog.Close>
              <Button
                color="gray"
                highContrast
                onClick={handleUpdateRegressionSet}
                disabled={updateRegressionSetLoading}
              >
                {updateRegressionSetLoading
                  ? "Updating..."
                  : "Update Regression Set"}
              </Button>
            </Flex>
          </Dialog.Content>
        </Dialog>
      </Table.Cell>
    </Table.Row>
  );
}
